/** Pure chart data shaping shared by chart leaves and the chart element. */
import type { FmDiameterSeries, FmHistogramBase } from './chartTypes';

export const CUT_KEY = 'cut';
export const KEPT_KEY = 'kept';
export const MODEL_KEY = 'model';

type HistogramRow = Record<string, number | string | undefined>;

export function speciesKey(code: number): string {
  return `species-${code}`;
}

function binMap(series: readonly FmDiameterSeries[]): Map<number, Map<number, number>> {
  const map = new Map<number, Map<number, number>>();
  for (const entry of series) {
    for (const bin of entry.bins) {
      const perSpecies = map.get(bin.diameter_cm) ?? new Map<number, number>();
      perSpecies.set(entry.speciesCode, bin.n_per_ha);
      map.set(bin.diameter_cm, perSpecies);
    }
  }
  return map;
}

function sortedDiameters(series: readonly FmDiameterSeries[]): number[] {
  return [...new Set(series.flatMap((entry) => entry.bins.map((bin) => bin.diameter_cm)))].sort(
    (a, b) => a - b,
  );
}

/**
 * Rows for Recharts: one per diameter class, the species' staying stems (or
 * their sum, `stayingAs: 'kept'`) with the served removed stems summed on top.
 * Every number is a served count.
 */
export function histogramRows(
  distributions: readonly FmDiameterSeries[],
  removedDistributions: readonly FmDiameterSeries[] | undefined,
  modelLine: FmHistogramBase['modelLine'],
  stayingAs: 'species' | 'kept' = 'species',
): HistogramRow[] {
  const staying = binMap(distributions);
  const removed = removedDistributions ? binMap(removedDistributions) : undefined;
  const model = new Map((modelLine?.points ?? []).map((bin) => [bin.diameter_cm, bin.n_per_ha]));
  return sortedDiameters([...distributions, ...(removedDistributions ?? [])]).map((diameter) => {
    const row: HistogramRow = { diameter: String(Math.round(diameter)) };
    let kept = 0;
    for (const [code, nStaying] of staying.get(diameter) ?? []) {
      if (stayingAs === 'kept') kept += nStaying;
      else if (nStaying > 0) row[speciesKey(code)] = nStaying;
    }
    if (kept > 0) row[KEPT_KEY] = kept;
    let cut = 0;
    for (const nRemoved of removed?.get(diameter)?.values() ?? []) cut += nRemoved;
    if (cut > 0) row[CUT_KEY] = cut;
    const modelValue = model.get(diameter);
    if (modelValue !== undefined) row[MODEL_KEY] = modelValue;
    return row;
  });
}

/** Share of a donut slice as a whole percent. */
export function donutShare(value: number, total: number): string {
  return `${Math.round((value / total) * 100)}%`;
}
